import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import APRmemo from "./APRComment.js/APRmemo";
import APRComment from "./APRComment.js/AprComments";
import axios from "../../api/axios";
import { getAttributeValue, getYearFromDateValue } from "../../utils/utils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const STAFF_PROGRAM = "AJDfCnHCQ2j";

const staffStrengths = [
  { department: "Central Administration", minimum: 111, maximum: 156, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Human Resource", minimum: 3, maximum: 4, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Statistics", minimum: 3, maximum: 5, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Social Welfare and Community Development", minimum: 10, maximum: 13, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Works", minimum: 57, maximum: 84, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Finance", minimum: 28, maximum: 45, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Birth and Death", minimum: 2, maximum: 5, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Physical Planning", minimum: 17, maximum: 24, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Agriculture", minimum: 52, maximum: 78, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Trade and Industry", minimum: 12, maximum: 22, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Transport", minimum: 9, maximum: 11, actual: 0, percentCovered: 0, trainingRequired: "" },
  // { department: "Health", minimum: 10, maximum: 15, actual: 0, percentCovered: 0, trainingRequired: "" },
  { department: "Total", minimum: 304, maximum: 447, actual: 0, percentCovered: 0, trainingRequired: "" },
];

function truncateLabel(label, maxLength = 24) {
  const text = String(label || "");
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function getRegistrationDateValue(member) {
  const registrationDate = getAttributeValue("Registration Date", member);
  if (registrationDate && registrationDate !== "N/A") {
    return registrationDate;
  }

  const registrationDateAlt = getAttributeValue("Registration date", member);
  if (registrationDateAlt && registrationDateAlt !== "N/A") {
    return registrationDateAlt;
  }

  const enrollment = (member.enrollments || []).find(
    (entry) => (entry?.program?.id || entry?.program) === STAFF_PROGRAM,
  ) || member.enrollments?.[0];

  return enrollment?.enrolledAt || member.createdAt || "";
}

function isRegisteredOnOrBeforeYear(member, selectedYear) {
  const registrationYear = getYearFromDateValue(getRegistrationDateValue(member));
  if (registrationYear == null) {
    return true;
  }

  return registrationYear <= selectedYear;
}

function buildStaffStrengthTable(members, selectedYear) {
  const counts = members.reduce((acc, { department }) => {
    if (!department || department === "N/A") {
      return acc;
    }
    acc[department] = (acc[department] || 0) + 1;
    return acc;
  }, {});

  let updatedStaffStrengths = staffStrengths.map((item) => {
    if (item.department === "Total") {
      return item;
    }

    const actual = counts[item.department] || 0;
    const percentCovered = item.minimum > 0 ? (actual / item.minimum) * 100 : 0;

    return {
      ...item,
      actual,
      percentCovered: percentCovered.toFixed(2),
    };
  });

  const totalActual = updatedStaffStrengths
    .filter((item) => item.department !== "Total")
    .reduce((sum, item) => sum + item.actual, 0);

  return updatedStaffStrengths.map((item) => {
    if (item.department !== "Total") {
      return item;
    }

    const percentCovered = item.minimum > 0 ? (totalActual / item.minimum) * 100 : 0;
    return {
      ...item,
      actual: totalActual,
      percentCovered: percentCovered.toFixed(2),
    };
  });
}

const Table_18 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);
  const [showChart, setShowChart] = useState(false);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getData();
  }, [district, selectedYear, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_18",
      tableData,
    }),
  }));

  async function getData() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const endDate = `${selectedYear}-12-31`;

      const response = await axios.get(
        `/tracker/trackedEntities?orgUnit=${district}&program=${STAFF_PROGRAM}&endDate=${endDate}&pageSize=5000`,
      );

      const data = response.data.instances || [];
      const members = data
        .filter((member) => isRegisteredOnOrBeforeYear(member, selectedYear))
        .map((member) => ({
          name: `${getAttributeValue("First Name", member)} ${getAttributeValue("Last Name", member)}`.trim(),
          department: getAttributeValue("Department", member),
          gender: getAttributeValue("Sex", member),
          registrationDate: getRegistrationDateValue(member),
        }));

      setTableData(buildStaffStrengthTable(members, selectedYear));
    } catch (err) {
      console.error("Error loading Table 18 data:", err);
      setTableData([]);
    }
  }

  const chartRows = tableData.filter((row) => row.department !== "Total");

  const chartData = {
    labels: chartRows.map((row) => truncateLabel(row.department)),
    datasets: [
      {
        label: "Minimum Required",
        data: chartRows.map((row) => row.minimum),
        backgroundColor: "#0d6efd",
        borderColor: "#0a58ca",
        borderWidth: 1,
      },
      {
        label: `Actual ${selectedYear}`,
        data: chartRows.map((row) => row.actual),
        backgroundColor: "#198754",
        borderColor: "#146c43",
        borderWidth: 1,
      },
      {
        label: "Maximum Required",
        data: chartRows.map((row) => row.maximum),
        backgroundColor: "#ffc107",
        borderColor: "#d39e00",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Staff Strength Analysis, ${selectedYear}`,
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const index = items[0]?.dataIndex;
            return chartRows[index]?.department || items[0]?.label;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        title: {
          display: true,
          text: "Number of Staff",
        },
      },
    },
  };

  return (
    <div className="col-12">
      <h3>Table 18: Staff Strengths</h3>
      <div className="card">
        <div className="card-header" />
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table18-${year}`}
            hideTableDis={hideTableDis}
          />
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead style={{ backgroundColor: "#d4edda", fontWeight: "bold", border: "1px solid #000" }}>
                <tr>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Department</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Maximum Required</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Minimum Required</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Actual {selectedYear}</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>% Covered</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Training Required</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #000" }}>{row.department}</td>
                      <td style={{ border: "1px solid #000" }}>{row.maximum}</td>
                      <td style={{ border: "1px solid #000" }}>{row.minimum}</td>
                      <td style={{ border: "1px solid #000" }}>{row.actual}</td>
                      <td style={{ border: "1px solid #000" }}>{row.percentCovered}</td>
                      <td style={{ border: "1px solid #000" }}>{row.trainingRequired}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ border: "1px solid #000", textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button className="btn btn-primary mt-3 no-print" onClick={() => setShowChart(!showChart)}>
            {showChart ? "Hide chart" : "Show optional chart"}
          </button>
          {showChart && (
            <div style={{ height: "420px" }} className="mt-3">
              {chartRows.length > 0 ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <p className="text-center">No chart data available for {selectedYear}</p>
              )}
            </div>
          )}

          <p className="mt-2">
            <small>Source: {selectedYear} HRMIS Reports</small>
          </p>
          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table18-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_18;
